import { useState, useCallback, useRef, useMemo } from "react"
import type { SearchableSelectOption } from "@/components/ats-fbo/fields/searchable-select"
import { logger } from "@/lib/logger"

export interface ODataSearchResult<T = undefined> {
  options: SearchableSelectOption[]
  items: T[]
  loading: boolean
  search: (term: string) => void
}

/**
 * Generic hook for OData-backed searchable selects.
 *
 * The caller's `fetchResults` should include the selected ID in the API query
 * so the selected item is always present in the response — no client-side
 * caching needed.
 */
export function useODataSearch<T>(
  fetchResults: (term: string) => Promise<T[]>,
  toOption: (item: T) => SearchableSelectOption,
): ODataSearchResult<T> {
  const [options, setOptions] = useState<SearchableSelectOption[]>([])
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const requestId = useRef(0)

  const search = useCallback(
    (term: string) => {
      const id = ++requestId.current
      setLoading(true)
      fetchResults(term)
        .then((data) => {
          if (id !== requestId.current) return
          setItems(data)
          setOptions(data.map(toOption))
        })
        .catch((err: unknown) => {
          if (id !== requestId.current) return
          logger.error(err)
          setItems([])
          setOptions([])
        })
        .finally(() => {
          if (id !== requestId.current) return
          setLoading(false)
        })
    },
    [fetchResults, toOption]
  )

  return { options, items, loading, search }
}

/**
 * Mock variant — filters a local array client-side.
 * The selected item is always included in the results.
 */
export function useMockSearch<TItem extends { id: number | string; name: string }, TOut = undefined>(
  mockData: TItem[],
  toOption: (item: TItem) => SearchableSelectOption,
  selectedId?: string | null,
  filterFn?: (item: TItem, lower: string) => boolean,
  toOut?: (items: TItem[]) => TOut[]
): ODataSearchResult<TOut> {
  const [searchResults, setSearchResults] = useState<SearchableSelectOption[]>([])
  const [searchItems, setSearchItems] = useState<TOut[]>([])

  const defaultFilter = (item: TItem, lower: string) =>
    item.name.toLowerCase().includes(lower)
  const matchFn = filterFn ?? defaultFilter

  const search = useCallback(
    (term: string) => {
      const lower = term.toLowerCase()
      const filtered = (term || filterFn) ? mockData.filter((o) => matchFn(o, lower)) : mockData
      setSearchResults(filtered.map(toOption))
      setSearchItems(toOut ? toOut(filtered) : (filtered as unknown as TOut[]))
    },
    [mockData, matchFn, toOption, toOut, filterFn]
  )

  // Ensure selected item is always in options
  const options = useMemo(() => {
    if (!selectedId) return searchResults
    if (searchResults.some((r) => r.id === selectedId)) return searchResults
    const source = mockData.find((o) => String(o.id) === selectedId)
    if (!source) return searchResults
    return [toOption(source), ...searchResults]
  }, [searchResults, selectedId, mockData, toOption])

  const items = useMemo(() => {
    if (!selectedId) return searchItems
    if (searchResults.some((r) => r.id === selectedId)) return searchItems
    const source = mockData.find((o) => String(o.id) === selectedId)
    if (!source) return searchItems
    const out = toOut ? toOut([source]) : ([source] as unknown as TOut[])
    return [...out, ...searchItems]
  }, [searchItems, searchResults, selectedId, mockData, toOut])

  return { options, items, loading: false, search }
}
